import { SubscriptionStatus } from './enums';

// State machine: valid status transitions
export const VALID_TRANSITIONS: Record<SubscriptionStatus, SubscriptionStatus[]> = {
  [SubscriptionStatus.TRIAL]: [
    SubscriptionStatus.ACTIVE,
    SubscriptionStatus.CANCELED,
    SubscriptionStatus.EXPIRED,
  ],
  [SubscriptionStatus.ACTIVE]: [
    SubscriptionStatus.CANCELED,
    SubscriptionStatus.EXPIRED,
    SubscriptionStatus.PAST_DUE,
    SubscriptionStatus.FREE_TIER,
  ],
  [SubscriptionStatus.PAST_DUE]: [
    SubscriptionStatus.ACTIVE,
    SubscriptionStatus.EXPIRED,
    SubscriptionStatus.CANCELED,
    SubscriptionStatus.FREE_TIER,
  ],
  [SubscriptionStatus.CANCELED]: [], // Terminal state
  [SubscriptionStatus.EXPIRED]: [], // Terminal state
  [SubscriptionStatus.FREE_TIER]: [
    SubscriptionStatus.ACTIVE, // Can resubscribe
    SubscriptionStatus.CANCELED, // Can explicitly cancel
  ],
};

// Helper to validate transitions
export function canTransition(from: SubscriptionStatus, to: SubscriptionStatus): boolean {
  return VALID_TRANSITIONS[from].includes(to);
}

export function validateTransition(from: SubscriptionStatus, to: SubscriptionStatus): void {
  if (!canTransition(from, to)) {
    throw new Error(`Invalid transition from ${from} to ${to}`);
  }
}

// Feature registry - 9 features for v2.0 (Phase 16 added TAX_INVOICES)
export const FEATURE_KEYS = {
  ANALYTICS_ADVANCED: 'analytics_advanced',
  BOOKING_AUTOMATION: 'booking_automation',
  MULTI_LOCATION: 'multi_location',
  CUSTOM_BRANDING: 'custom_branding',
  PRIORITY_SUPPORT: 'priority_support',
  API_ACCESS: 'api_access',
  STAFF_MANAGEMENT: 'staff_management',
  REPORTING_EXPORT: 'reporting_export',
  TAX_INVOICES: 'tax_invoices',
} as const;

export type FeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS];

// All features for Legacy Free plan
export const LEGACY_FREE_FEATURES: FeatureKey[] = Object.values(FEATURE_KEYS);

// Plan limits
export const PLAN_LIMITS = {
  MAX_PLANS: 50,
  MAX_FEATURES_PER_PLAN: 20,
  MAX_PLAN_NAME_LENGTH: 100,
  MAX_DESCRIPTION_LENGTH: 500,
  MIN_TRIAL_DAYS: 0,
  MAX_TRIAL_DAYS: 90,
} as const;

// Payment retry schedule per CONTEXT.md: Day 0 (initial) -> Day 1 -> Day 3 -> Day 7
export const RETRY_DELAYS_DAYS = [1, 2, 4] as const; // Days to add from previous attempt
export const MAX_RETRY_ATTEMPTS = 3;

// Payment limits per ICICI gateway constraints
export const UPI_AUTO_DEBIT_LIMIT = 15000; // Rs 15K without PIN
export const UPI_AUTOPAY_MAX_LIMIT = 200000; // Rs 2L with PIN
export const CARD_SI_LIMIT = 15000; // Rs 15K hard limit

// Pre-debit notification timing
export const PRE_DEBIT_BUFFER_HOURS = 26; // 24h + 2h buffer for network delays
